import React, { useState } from "react";
import { ReactLocalization, useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";
import Account from "byzantine/src/Account";
import utils from "../../utils";
import InformationRow, { InformationRowProps } from "../InformationRow";
import { useInstitutionSettings } from "../contexts/InstitutionSettingsContext";
import SectionCard from "../SectionCard";

interface ExpandButtonProps {
  isExpanded: boolean;
  setIsExpanded: (oldState: boolean) => void;
  canBeExpanded?: boolean;
}

const ExpandButton = ({
  isExpanded,
  setIsExpanded,
  canBeExpanded = true,
}: ExpandButtonProps) => {
  const { l10n } = useLocalization();

  return canBeExpanded ? (
    <Button
      kind="plain"
      onClick={() => setIsExpanded(!isExpanded)}
      label={
        isExpanded
          ? l10n.getString("button-hide-details", null, "Hide details")
          : l10n.getString("button-more-details", null, "More details")
      }
      testId="expand-account-details"
    />
  ) : null;
};

export const translateAccountDetailsLabel = (
  l10n: ReactLocalization,
  label?: string,
  fluentId?: string
) => {
  if (!label || !fluentId) {
    return "";
  }
  return l10n.getString(fluentId, null, label);
};

interface AccountMetadataProps {
  metadata: InformationRowProps | InformationRowProps[];
}

const AccountMetadata = ({ metadata }: AccountMetadataProps) =>
  Array.isArray(metadata) ? (
    metadata
      .filter(({ label }) => !!label)
      .map(({ label, value }) => (
        <InformationRow key={label} label={label} value={value} />
      ))
  ) : (
    <InformationRow label={metadata?.label} value={metadata?.value} />
  );

interface AccountDetailsCardProps {
  account: Account;
}
const AccountDetailsCard = ({ account }: AccountDetailsCardProps) => {
  const { l10n } = useLocalization();
  const {
    account_details_information_blacklist: accountDetailsInformationBlacklist,
  } = useInstitutionSettings();
  const [isExpanded, setIsExpanded] = useState(false);

  let title = null;
  if (
    ["checking", "savings", "money_market", "certificate_of_deposit"].includes(
      account.product_type
    )
  ) {
    title = l10n.getString("heading-account-details", null, "Account details");
  } else if (account.product_type === "loan") {
    title = l10n.getString("heading-loan-details", null, "Loan details");
  } else if (account.product_type === "line_of_credit") {
    title = l10n.getString(
      "heading-line-of-credit-details",
      null,
      "Line of credit details"
    );
  } else if (account.product_type === "credit_card") {
    title = l10n.getString(
      "heading-credit-card-details",
      null,
      "Credit card details"
    );
  } else {
    title = l10n.getString("heading-details", null, "Details");
  }

  const { defaultDetails, expandedDetails } = account.accountDetails(
    utils.getNDSFormatters(),
    accountDetailsInformationBlacklist
  );

  return (
    <SectionCard>
      <SectionCard.Title
        text={title}
        button={
          <ExpandButton
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            canBeExpanded={expandedDetails.length > 0}
          />
        }
      />
      <div>
        <AccountMetadata
          metadata={defaultDetails.map((detail) => ({
            label: translateAccountDetailsLabel(
              l10n,
              detail?.label,
              detail?.fluentId
            ),
            value: detail?.value,
          }))}
        />
        {isExpanded && (
          <AccountMetadata
            metadata={expandedDetails.map((detail) => ({
              label: translateAccountDetailsLabel(
                l10n,
                detail?.label,
                detail?.fluentId
              ),
              value: detail?.value,
            }))}
          />
        )}
      </div>
    </SectionCard>
  );
};
export default AccountDetailsCard;
